import {
  Box,
  Dialog,
  DialogContent,
  Grid,
  styledComponent,
  TextField,
  Typography,
  Button,
  useTheme
} from '@hermes/web-components'
import { ChangeEvent, PropsWithChildren, useEffect, useRef, useState } from 'react'
import useTranslateMessage from '../../../../hooks/useTranslateMessage'
import FullWidthContainer from '../../../FullWidthContainer'
import { SearchResultsMobileTitle } from './styles'
import { TEST_IDS } from '../../../../__tests__/testUtils/constants'
import useSearchBarConfig from '../../../search/hooks/useSearchBarConfig'
import ArrowRight from '../../../Icons/ArrowRightIcon'
import Header from '../../../../components/Header'

interface MobileResultProps extends PropsWithChildren {
  isOpen: boolean
  heading?: string
  searchString: string
  onClose: () => void
  onChangeSearch: (ev: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void
}

const StyledDialog = styledComponent(Dialog)(({ theme }) => ({
  [theme.breakpoints.down('lg')]: {
    '& .MuiDialog-paper': {
      margin: 0,
      width: '100%',
      height: '100%',
      borderRadius: 0,
      maxHeight: 'none'
    },
    '& .MuiDialogContent-root': {
      width: '100%',
      paddingTop: 0
    }
  }
}))

const MobileSearchTextField = styledComponent(TextField)(() => ({
  height: '40px',
  borderRadius: '24px',
  marginTop: 8,
  '& .MuiInputBase-root': {
    borderRadius: '24px',
    height: '40px'
  }
}))

const MobileSearchButton = styledComponent(Button)(() => ({
  height: '40px',
  width: '40px',
  minWidth: '40px',
  backgroundColor: '#FFFFFF',
  borderRadius: '50%',
  border: '1px solid #D5DEF9'
}))

const MobileSearchContainer = styledComponent(Box)(() => ({
  width: '100%',
  display: 'flex',
  gap: '8px',
  alignItems: 'flex-end'
}))

const ArrowRightCustom = styledComponent(ArrowRight)(() => ({
  width: '15px',
  height: '15px',
  fillPath: 'black',
  transform: 'scaleX(-1)'
}))

const zeroMargin = '0!important'

export const MobileResultAutocomplete = ({
  children,
  heading,
  searchString,
  isOpen,
  onClose,
  onChangeSearch
}: MobileResultProps) => {
  const translate = useTranslateMessage()
  const theme = useTheme()
  const [searchValue, setSearchValue] = useState('')
  const searchInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    setSearchValue(searchString)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const { searchIconPosition, MobileKeywordSearchLeftIconAlternative } = useSearchBarConfig()

  return (
    <StyledDialog
      open={isOpen}
      data-testid={TEST_IDS.MOBILE_RESULTS_AUTOCOMPLETE}
      onClose={onClose}
      maxWidth="lg"
      sx={{ pt: 0 }}
    >
      <DialogContent sx={{ p: 0, width: '100vw', backgroundColor: 'common.white', position: 'relative' }}>
        <FullWidthContainer
          containerSx={{
            justifyContent: 'flex-start',
            flexDirection: 'column',
            marginLeft: zeroMargin,
            marginRight: zeroMargin,
            maxWidth: '100%!important'
          }}
          sx={{
            pb: 2,
            marginLeft: zeroMargin,
            marginRight: zeroMargin,
            ...(theme?.custom?.SearchBar?.mobile?.searchResultAutocompleteModal ?? {})
          }}
        >
          {heading && (
            <Grid
              container
              sx={{
                ...SearchResultsMobileTitle,
                justify: 'space-between',
                alignItems: 'center'
              }}
            >
              <Typography>{heading}</Typography>
            </Grid>
          )}
          <Box sx={{ ...(theme?.custom?.SearchBar?.mobile?.searchResultAutocompleteModal?.backButtonWrapper ?? {}) }}>
            <Header />
            <MobileSearchContainer>
              <MobileSearchButton onClick={onClose}>
                <ArrowRightCustom
                  color={theme?.custom?.SearchBar?.mobile?.searchResultAutocompleteModal?.arrowRight.color}
                />
              </MobileSearchButton>
              <MobileSearchTextField
                fullWidth
                placeholder={translate('search')}
                onChange={(e) => {
                  onChangeSearch(e)
                  setSearchValue(e.target.value)
                }}
                value={searchValue}
                inputProps={{
                  ref: searchInputRef
                }}
                InputProps={{
                  sx: {
                    '.MuiOutlinedInput-notchedOutline': {
                      borderColor: 'text.black200',
                      borderLeftWidth: { xs: 1, md: 0 }
                    }
                  },
                  [searchIconPosition]: MobileKeywordSearchLeftIconAlternative ? (
                    <MobileKeywordSearchLeftIconAlternative
                      sx={{
                        color: theme.custom?.SearchFilters?.mobile?.textColor,
                        width: '16px',
                        height: '16px',
                        marginLeft: '8px',
                        marginRight: '4px'
                      }}
                    />
                  ) : null
                }}
              />
            </MobileSearchContainer>
          </Box>
        </FullWidthContainer>
        <Box onTouchStart={() => searchInputRef.current?.blur()}>{children}</Box>
      </DialogContent>
    </StyledDialog>
  )
}
